import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Space } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Select,
  Modal,
  Form,
  Input,
  InputNumber,
  useBreadcrumbRoutes,
  Wrapper,
  Button,
  FormTitle,
  Tooltip,
} from '@maxtropy/components';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { CycleType, useDeviationOptions } from './service';
import styles from './index.module.scss';
import {
  apiV2DeviationRateAnalysisConfigRuleQueryPost,
  apiV2DeviationRateAnalysisConfigRuleUpsertPost,
  V2DeviationRateAnalysisConfigRuleUpsertPostRequest,
  apiV2DeviationRateAnalysisGetEnergyMediumsPost,
  apiV2DeviationRateAnalysisGetEnergyMediumTopologyNodePost,
} from '@maxtropy/device-customer-apis-v2';

interface Props {
  isEdit?: boolean;
}

const DeviationAlarmCreate: React.FC<Props> = ({ isEdit }) => {
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const routes = useMemo(() => {
    return [{ name: `${isEdit ? '编辑' : '新建'}规则` }];
  }, [isEdit]);

  const { loading: optionsLoading, optionsMap, fetchCascadeOptions } = useDeviationOptions();
  // 能源介质拓扑
  const energyMediumTopologyId = Form.useWatch('energyMediumTopologyId', form);
  // 拓扑节点
  const energyMediumTopologyNodeIds = Form.useWatch('energyMediumTopologyNodeIds', form);
  // 统计周期类型
  const statCycleType = Form.useWatch('statCycleType', form);

  const onFinish = async (values: V2DeviationRateAnalysisConfigRuleUpsertPostRequest) => {
    setLoading(true);
    apiV2DeviationRateAnalysisConfigRuleUpsertPost({
      id,
      ...values,
    })
      .then(() => {
        handleCancel();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCancel = () => {
    navigate(`/deviation/server/alarm`);
  };

  // 能源介质拓扑change
  const handleEnergyMediumTopologyChange = (energyMediumTopologyId: number) => {
    fetchCascadeOptions('topologyNodeIds', () =>
      apiV2DeviationRateAnalysisGetEnergyMediumTopologyNodePost({ energyMediumTopologyId: [energyMediumTopologyId] })
    );
    fetchCascadeOptions('energyMediums', () =>
      apiV2DeviationRateAnalysisGetEnergyMediumsPost({
        energyMediumTopologyId: [energyMediumTopologyId],
        topologyNodeIds: energyMediumTopologyNodeIds,
      })
    );
    form.setFieldValue('energyMediumTopologyNodeIds', []);
    form.setFieldValue('energyMediumId', undefined);
  };

  // 拓扑节点change
  const handleTopologyNodeChange = (topologyNodeIds: number[]) => {
    fetchCascadeOptions('energyMediums', () =>
      apiV2DeviationRateAnalysisGetEnergyMediumsPost({
        energyMediumTopologyId: [energyMediumTopologyId],
        topologyNodeIds,
      })
    );
    form.setFieldValue('energyMediumId', undefined);
  };

  useEffect(() => {
    if (id && isEdit) {
      setLoading(true);
      apiV2DeviationRateAnalysisConfigRuleQueryPost({ id }).then(res => {
        // 转换 topologyNodes 为 energyMediumTopologyNodeIds
        const { energyMediumTopologyId, topologyNodes, ruleName: name, ...rest } = res ?? {};
        const energyMediumTopologyNodeIds = topologyNodes?.map(node => node.id!) ?? [];
        form.setFieldsValue({
          ...rest,
          name,
          energyMediumTopologyId,
          energyMediumTopologyNodeIds,
        });

        let count = 0;
        const finish = () => {
          count++;
          count === 2 && setLoading(false);
        };

        // 拓扑节点的选项要收缩
        fetchCascadeOptions('topologyNodeIds', () =>
          apiV2DeviationRateAnalysisGetEnergyMediumTopologyNodePost({
            energyMediumTopologyId: [energyMediumTopologyId!],
          })
        ).then(finish);

        // 能源介质的选项要收缩
        fetchCascadeOptions('energyMediums', () =>
          apiV2DeviationRateAnalysisGetEnergyMediumsPost({
            energyMediumTopologyId: [energyMediumTopologyId!],
            topologyNodeIds: energyMediumTopologyNodeIds,
          })
        ).then(finish);
      });
    }
  }, [id, isEdit]);

  return (
    <>
      <Wrapper
        routes={[...(breadcrumbRoutes?.routes ?? []), ...routes]}
        className={styles.create_wrapper}
        isLoading={loading}
      >
        <FormTitle title={routes[0].name}></FormTitle>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          style={{ paddingLeft: 32 }}
          initialValues={{ statCycleType: CycleType.hour }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="规则名称"
                name="name"
                rules={[
                  { required: true, message: '请输入规则名称' },
                  { max: 50, message: '最多输入五十个字符' },
                ]}
              >
                <Input placeholder="请输入规则名称"></Input>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="报警等级" name="alarmLevel" rules={[{ required: true, message: '请选择报警等级' }]}>
                <Select placeholder="请选择" options={optionsMap.alarmLevel} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={
                  <span>
                    偏差率&nbsp;
                    <Tooltip
                      overlayInnerStyle={{ width: 320 }}
                      title="根据设定的统计周期, 查询统计周期内所选择能源介质拓扑对应的拓扑节点对应的能源介质的偏差率绝对值, 若大于等于输入的值, 则报警；"
                    >
                      <ExclamationCircleOutlined style={{ color: 'var(--warning-color)' }} />
                    </Tooltip>
                  </span>
                }
                name="deviationRate"
                rules={[{ required: true, message: '请输入偏差率' }]}
              >
                <InputNumber placeholder="请输入" min={0} precision={2} addonAfter="%" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="统计周期" required>
                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item
                      name="statCycleType"
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: '请选择统计周期' }]}
                    >
                      <Select
                        placeholder="请选择"
                        options={[
                          { label: '按天', value: CycleType.date },
                          { label: '按小时', value: CycleType.hour },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="statCycleTime"
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: '请输入周期值' }]}
                    >
                      <InputNumber
                        placeholder="请输入"
                        min={1}
                        addonAfter={statCycleType === CycleType.date ? '天' : '小时'}
                        style={{ width: '100%' }}
                        precision={0}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="能源介质拓扑"
                name="energyMediumTopologyId"
                rules={[
                  {
                    required: true,
                    message: '请选择能源介质拓扑',
                  },
                ]}
              >
                <Select
                  placeholder="请选择"
                  options={optionsMap.energyMediumTopologyId}
                  showSearch
                  optionFilterProp="label"
                  onChange={handleEnergyMediumTopologyChange}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="拓扑节点"
                name="energyMediumTopologyNodeIds"
                rules={[{ required: true, message: '请选择拓扑节点' }]}
              >
                <Select
                  placeholder="请选择"
                  mode="multiple"
                  options={energyMediumTopologyId ? optionsMap.topologyNodeIds : []}
                  showSearch
                  optionFilterProp="label"
                  onChange={handleTopologyNodeChange}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="能源介质" name="energyMediumId" rules={[{ required: true, message: '请选择能源介质' }]}>
                <Select
                  placeholder="请选择"
                  options={
                    energyMediumTopologyId && energyMediumTopologyNodeIds?.length ? optionsMap.energyMediums : []
                  }
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Space className="sticky-footer" size={8}>
          <Button
            type="primary"
            onClick={() => {
              form.submit();
            }}
          >
            保存
          </Button>
          <Button
            onClick={() => {
              Modal.confirm({
                title: <div>是否放弃所有未保存信息并返回列表？</div>,
                onOk: handleCancel,
              });
            }}
          >
            取消
          </Button>
        </Space>
      </Wrapper>
    </>
  );
};

export default DeviationAlarmCreate;
